/**
 * How a Pod Position reads in the UI. "R1" only says which seat; the seat's Discipline says
 * what kind of person belongs in it, so we show both where there is room ("R1 · SA") and
 * the Discipline alone where there is not ("SA", with the full text on hover).
 *
 * Supporting has no Discipline seat, and a seat with no Discipline set keeps its plain label,
 * so every function here degrades to what the UI showed before.
 */

export interface PositionDiscipline {
  code: string;
  name: string;
}

/** Seat → Discipline for one Solution. Only R1–R4 appear; Supporting never does. */
export type PositionDisciplines = Partial<Record<string, PositionDiscipline>>;

function disciplineFor(
  role: string,
  disciplines: PositionDisciplines | null | undefined,
): PositionDiscipline | null {
  if (role === "Supporting") return null;
  return disciplines?.[role] ?? null;
}

/** "R1 · SA" — for rows, headings, dropdowns and copy. */
export function positionLabel(
  role: string,
  disciplines: PositionDisciplines | null | undefined,
): string {
  const d = disciplineFor(role, disciplines);
  return d === null ? role : `${role} · ${d.code}`;
}

/** "SA" — for chips too small for both; falls back to the role. */
export function positionShortLabel(
  role: string,
  disciplines: PositionDisciplines | null | undefined,
): string {
  return disciplineFor(role, disciplines)?.code ?? role;
}

/** "R1 · Solution Architect" — the tooltip behind a compact chip. */
export function positionTitle(
  role: string,
  disciplines: PositionDisciplines | null | undefined,
): string {
  const d = disciplineFor(role, disciplines);
  return d === null ? role : `${role} · ${d.name}`;
}

/** The seat → Discipline map a plan's Planned Pod already carries. */
export function disciplinesFromPlannedPod(
  positions:
    | readonly {
        positionCode: string;
        expectedDisciplineCode: string;
        expectedDisciplineName: string;
      }[]
    | null
    | undefined,
): PositionDisciplines {
  const out: PositionDisciplines = {};
  for (const p of positions ?? []) {
    out[p.positionCode] = { code: p.expectedDisciplineCode, name: p.expectedDisciplineName };
  }
  return out;
}
